/**
 * Party — Manages the player's party of characters, companions,
 * pack animals, and vehicle.
 *
 * @module Party
 */

const MAX_MEMBERS = 4;
const MAX_PACK_ANIMALS = 2;

export default class Party {
  constructor() {
    /** @type {import('./Character.js').default[]} */
    this.members = [];

    /** @type {object[]} Companion data objects */
    this.companions = [];

    /** @type {(object|null)[]} Pack animal slots (2 max) */
    this.packAnimals = [null, null];

    /** @type {object|null} Vehicle slot (1 max) */
    this.vehicle = null;

    /** Shared party level. */
    this.partyLevel = 1;

    /** Shared gold pool. */
    this.gold = 0;
  }

  /**
   * Add a character to the party.
   * @param {import('./Character.js').default} character
   * @returns {boolean} true if added successfully
   */
  addMember(character) {
    if (this.members.length >= MAX_MEMBERS) return false;
    this.members.push(character);
    character.level = this.partyLevel;
    return true;
  }

  /**
   * Remove a character from the party by index.
   * @param {number} index
   * @returns {import('./Character.js').default|null} the removed character
   */
  removeMember(index) {
    if (index < 0 || index >= this.members.length) return null;
    return this.members.splice(index, 1)[0];
  }

  /**
   * Swap the positions of two party members.
   * @param {number} i
   * @param {number} j
   */
  swapMembers(i, j) {
    if (i < 0 || i >= this.members.length) return;
    if (j < 0 || j >= this.members.length) return;
    const temp = this.members[i];
    this.members[i] = this.members[j];
    this.members[j] = temp;
  }

  /**
   * Get all active (alive) party members.
   * @returns {import('./Character.js').default[]}
   */
  getActiveMembers() {
    return this.members.filter(m => m.hp > 0);
  }

  /**
   * Get the party leader (first member).
   * @returns {import('./Character.js').default|null}
   */
  getLeader() {
    return this.members.length > 0 ? this.members[0] : null;
  }

  /**
   * Level up the entire party.
   */
  levelUp() {
    this.partyLevel += 1;
    for (const member of this.members) {
      member.levelUp();
    }
  }

  /**
   * Add a pack animal.
   * @param {object} animal
   * @returns {boolean}
   */
  addPackAnimal(animal) {
    for (let i = 0; i < MAX_PACK_ANIMALS; i++) {
      if (!this.packAnimals[i]) {
        this.packAnimals[i] = animal;
        return true;
      }
    }
    return false;
  }

  /**
   * Remove a pack animal by slot index.
   * @param {number} slot
   * @returns {object|null}
   */
  removePackAnimal(slot) {
    if (slot < 0 || slot >= MAX_PACK_ANIMALS) return null;
    const animal = this.packAnimals[slot];
    this.packAnimals[slot] = null;
    return animal;
  }

  /**
   * Serialize the party to a plain object for saving.
   * @returns {object}
   */
  toJSON() {
    return {
      members: this.members.map(m => m.toJSON()),
      companions: [...this.companions],
      packAnimals: [...this.packAnimals],
      vehicle: this.vehicle,
      partyLevel: this.partyLevel,
      gold: this.gold,
    };
  }
}
